/* TypeScript types for the configuration system */

import React from 'react'
import { GenNode } from './carbon/models/GenNode'
import { TableViewSettingsData, BrandPaletteEntry } from './gotypes'

// Config hierarchy levels - matches backend enum
export enum ConfigType {
  Global = 0,
  Customer = 1,
  Job = 2,
  Folder = 3,
  Table = 4
}

export type ConfigFieldMergeStrategy = 'override' | 'merge'

export type TableViewMode = 'table' | 'debug'

// Custom persona for chat interface
export interface CustomPersona {
  name: string
  prompt: string
}

// Configuration data fields - matches backend ConfigData struct
// All fields are optional since configs can be partial and merge with defaults
export interface ConfigData {
  table_view_mode?: TableViewMode
  table_analysis_prompt?: string
  method_prompt?: string
  theme_ai_extract_prompt?: string
  theme_ai_match_prompt?: string
  exec_report_table_analysis_prompt?: string
  exec_report_section_synthesis_prompt?: string
  custom_personas?: CustomPersona[]
  include_side_label?: boolean
  auto_update_enabled?: boolean
  autocoder_carbon_labels?: string[]
  stats_reviewer_name?: string
  stats_reviewer_contact?: string
  stats_reviewer_team?: string
  stats_reviewer_email?: string
  table_view_settings?: TableViewSettingsData
  brand_palette?: Record<string, BrandPaletteEntry>
}

export const DEFAULT_CONFIG_DATA: ConfigData = {
  table_view_mode: 'table',
  table_analysis_prompt: '',
  method_prompt: '',
  theme_ai_extract_prompt: '',
  theme_ai_match_prompt: '',
  exec_report_table_analysis_prompt: '',
  exec_report_section_synthesis_prompt: '',
  custom_personas: [],
  include_side_label: true,
  auto_update_enabled: false,
  autocoder_carbon_labels: [],
}

export type ConfigFieldName = keyof ConfigData

// Main configuration record - matches backend Config struct
export interface Config {
  id: string
  config_type: ConfigType
  customer?: string
  job?: string
  folder_path?: string
  table_name?: string
  data: ConfigData
  created_at: number
  updated_at: number
}

// Query parameters for listing configs - matches backend ListConfigsQuery
export interface ListConfigsQuery {
  config_type?: ConfigType
  customer?: string
  job?: string
  folder_path?: string
  table_name?: string
}

// Tracks where a config field value originated from - matches backend ConfigReference
export interface ConfigReference {
  field_name: string
  value?: unknown
  source_entity: ListConfigsQuery
  source_config: Config
}

export interface MergedConfigFieldDetail {
  strategy: ConfigFieldMergeStrategy
  sources: ConfigReference[]
}

// Result of merged config with audit trail - matches backend MergedConfigResult
export interface MergedConfigResult {
  data: ConfigData
  fields: Record<string, MergedConfigFieldDetail>
}

// Query parameters for getting merged config - matches backend MergedConfigQuery
export interface MergedConfigQuery {
  config_type: ConfigType
  customer: string
  job?: string
  folder_path?: string
  table_name?: string
}

// UI-specific types for the frontend

// Represents an entity that can be configured (customer, job, folder, table)
export interface ConfigEntity {
  type: ConfigType
  customer: string
  job?: string
  folder_path?: string
  table_name?: string
  display_name: string // User-friendly name for the entity
  node?: GenNode // Optional: associated tree node
}

// Props for components
export interface ConfigManagerProps {
  // No props - uses account context
}

export interface ConfigTreeViewProps {
  toc: GenNode[]
  selectedNode: GenNode | null
  onNodeSelect: (node: GenNode) => void
  configuredNodes: Set<string> // Node IDs that have custom configs
  loading?: boolean
}

export interface ConfigEditorHandle {
  save: () => Promise<void>
  cancel: () => void
  hasChanges: () => boolean
}

export interface ConfigEditorProps {
  open: boolean
  onClose: () => void
  entity: ConfigEntity | null
  mergedConfig: MergedConfigResult | null
  onSave: (data: ConfigData) => Promise<void>
  onSaved?: () => void | Promise<void>
  handleNodeSettings?: (node: GenNode) => Promise<void>
  onEditEntity?: (entity: ConfigEntity) => Promise<void>
  loading?: boolean
  saving?: boolean
  variant?: 'modal' | 'form' // Controls layout: modal (Dialog with tabs) or form (page-embedded, all sections visible)
  hideActions?: boolean // When true, don't render the Cancel/Save buttons (for external action rendering)
  onChangeDetected?: (hasChanges: boolean) => void // Called when form changes are detected
  readOnly?: boolean // When true, disable all editable fields
  filterTabs?: string[] // Optional: Only show these tabs (by tab id)
  alwaysEnableSave?: boolean // When true, Save button is always enabled regardless of change detection
  hideHeader?: boolean // When true, hide the "Configure Job/Customer" header
  showSectionSave?: boolean // When true, show an inline Save button in each section header
  autoSave?: boolean // When true, save on field blur and select/switch change, hide save/cancel UI
}

export interface ConfigFieldSourceInfo {
  reference: ConfigReference
  isLocal: boolean
}

export interface ConfigFieldProps {
  fieldName: string
  label: string
  value: string | undefined
  strategy: ConfigFieldMergeStrategy
  sources: ConfigFieldSourceInfo[]
  onChange: (value: string) => void
  onShowSourceInfo?: (reference: ConfigReference, event?: React.MouseEvent<HTMLButtonElement>) => void
  multiline?: boolean
  rows?: number
  placeholder?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  onBlur?: () => void
}

export interface ConfigSourceInfoProps {
  reference: ConfigReference | null
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onEditSource: (entity: ConfigEntity) => void | Promise<void>
}